import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for smooth count-up animation using requestAnimationFrame
 * @param {number} end - The target value to count up to
 * @param {number} duration - Animation duration in milliseconds (default: 1000)
 * @param {number} decimals - Number of decimal places (default: 0)
 * @param {number} start - Starting value (default: 0)
 * @returns {number} Current animated value
 */
const useCountUpAnimation = (end, duration = 1000, decimals = 0, start = 0) => {
  const [count, setCount] = useState(start);
  const frameRef = useRef();
  const startTimeRef = useRef();
  const previousEndRef = useRef(end);

  useEffect(() => {
    // Cancel any ongoing animation
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }

    // If end value hasn't changed, don't re-animate
    if (previousEndRef.current === end && count !== start) {
      return;
    }

    previousEndRef.current = end;

    // Reset start time for new animation
    startTimeRef.current = null;

    // Easing function: easeOutQuart for smooth deceleration
    const easeOutQuart = (t) => {
      return 1 - Math.pow(1 - t, 4);
    };

    const animate = (currentTime) => {
      if (!startTimeRef.current) {
        startTimeRef.current = currentTime;
      }

      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // Apply easing
      const easedProgress = easeOutQuart(progress);

      // Calculate current value
      const currentValue = start + (end - start) * easedProgress;

      // Round to specified decimals
      const roundedValue = decimals === 0
        ? Math.round(currentValue)
        : Number(currentValue.toFixed(decimals));

      setCount(roundedValue);

      // Continue animation if not complete
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      }
    };

    // Start animation
    frameRef.current = requestAnimationFrame(animate);

    // Cleanup
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [end, duration, decimals, start]);

  return count;
};

export default useCountUpAnimation;
